import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { cn, StatusBadge, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { AdminMemberOverviewItem, AdminOrganizationListItem } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import {
  CalendarIcon,
  CheckSquareIcon,
  ClockIcon,
  KeyIcon,
  MailIcon,
  MembersIcon,
  OrganizationsIcon,
  TagIcon,
  TextIcon,
  WorkspacesIcon,
} from '@/components/layout/icons';
import { FilterBar, FilterPillGroup } from '@/components/filter-bar';
import { OrganizationFilterRow } from '@/components/organization-filter-row';
import { ListEmptyState, TestWorkspacesPill, WorkspaceLabel } from '@/components/test-workspaces';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { SEARCH_PARAM, readEnumParam, useListParams } from '@/lib/list-view/use-list-params';
import { ORGANIZATION_PARAM, useOrganizationFilter } from '@/lib/list-view/use-organization-filter';
import { useWorkspaceFilter } from '@/lib/list-view/use-workspace-filter';
import { ALL_WORKSPACES_QUERY, includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';

const DASH = '—';
const ACTIVITY_STATUSES = ['active', 'deactivated'] as const;
const ONBOARDING_FILTERS = ['complete', 'incomplete'] as const;

export const membersLoaderParams = [] as const;

export type MembersLoaderData = {
  readonly members: ReadonlyArray<AdminMemberOverviewItem>;
  // The Standort filter's options. Empty when the operator may not read Standorte.
  readonly organizations: ReadonlyArray<AdminOrganizationListItem>;
  readonly columnPreference: ColumnPreference;
};

export async function membersLoader(): Promise<MembersLoaderData> {
  const [result, organizations, columnPreference] = await Promise.all([
    apiClient.adminMembers.list({ query: includeTestWorkspacesQuery() }),
    // An options source, not a list of its own: every workspace, so the Standort
    // select names Standorte whatever the switch shows. A refused or failed read
    // degrades to no Standort filter rather than failing a page the operator may
    // open without `organization-read` (as the operator-name read on API-Keys).
    apiClient.adminOrganizations.list({ query: ALL_WORKSPACES_QUERY }).catch(() => null),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.members),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load members', { status: result.status });
  }

  return {
    members: result.body.data,
    organizations: organizations?.ok ? organizations.body.data : [],
    columnPreference,
  };
}

function matches(member: AdminMemberOverviewItem, query: string): boolean {
  if (query === '') return true;
  const haystack = [member.firstName, member.lastName, member.email, member.workspaceName].join(' ').toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function fullName(member: AdminMemberOverviewItem): string {
  return `${member.firstName} ${member.lastName}`.trim();
}

function useMemberColumns(): ReadonlyArray<OperatorColumn<AdminMemberOverviewItem>> {
  const t = useTranslations('pages.admin.members');
  const intl = useIntl();

  return useMemo(() => {
    const collator = new Intl.Collator(intl.locale);
    const roleLabel = (member: AdminMemberOverviewItem): string | null =>
      member.roles.length === 0
        ? null
        : member.roles
            .map((slug) => t(`roles.${slug}`))
            .sort(collator.compare)
            .join(', ');
    const empty = <span className="text-text-placeholder">{DASH}</span>;

    return [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        cell: (member) => (fullName(member) === '' ? DASH : fullName(member)),
        sortValue: (member) => (fullName(member) === '' ? null : fullName(member)),
      },
      {
        id: 'email',
        label: t('columns.email'),
        icon: MailIcon,
        cell: (member) => member.email,
        sortValue: (member) => member.email,
      },
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        cell: (member) => <WorkspaceLabel isTest={member.isTest}>{member.workspaceName}</WorkspaceLabel>,
        sortValue: (member) => member.workspaceName,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        cellClassName: 'whitespace-nowrap',
        cell: (member) => (
          <StatusBadge
            tone={member.status === 'active' ? 'success' : 'neutral'}
            label={t(`detail.status.${member.status}`)}
          />
        ),
        sortValue: (member) => t(`detail.status.${member.status}`),
      },
      {
        id: 'onboarding',
        label: t('columns.onboarding'),
        icon: CheckSquareIcon,
        cellClassName: 'whitespace-nowrap',
        cell: (member) =>
          member.onboardingOpenCount === 0 ? (
            <StatusBadge tone="success" label={t('onboarding.complete')} />
          ) : (
            <StatusBadge tone="warning" label={t('onboarding.open', { count: String(member.onboardingOpenCount) })} />
          ),
        sortValue: (member) => member.onboardingOpenCount,
      },
      {
        id: 'role',
        label: t('columns.role'),
        icon: KeyIcon,
        cell: (member) => roleLabel(member) ?? empty,
        sortValue: roleLabel,
      },
      {
        id: 'primaryOrganization',
        label: t('columns.primary-organization'),
        icon: OrganizationsIcon,
        cell: (member) => member.primaryOrganizationName ?? empty,
        sortValue: (member) => member.primaryOrganizationName,
      },
      {
        id: 'lastActive',
        label: t('columns.last-active'),
        icon: ClockIcon,
        cellClassName: 'whitespace-nowrap text-text-tertiary',
        cell: (member) =>
          member.lastActive === null
            ? t('last-active-never')
            : intl.formatDate(member.lastActive, { dateStyle: 'short' }),
        sortValue: (member) => (member.lastActive === null ? null : Date.parse(member.lastActive)),
      },
      {
        id: 'createdAt',
        label: t('columns.created-at'),
        icon: CalendarIcon,
        headClassName: 'w-[150px]',
        cellClassName: 'text-text-tertiary',
        cell: (member) => intl.formatDate(member.createdAt, { dateStyle: 'short' }),
        sortValue: (member) => Date.parse(member.createdAt),
      },
    ];
  }, [t, intl]);
}

export function MembersPage() {
  const t = useTranslations('pages.admin.members');
  const tFilters = useTranslations('pages.admin.shell.filters');
  const { members, organizations, columnPreference } = useLoaderData() as MembersLoaderData;
  const navigate = useNavigate();

  const { params, setParams } = useListParams();
  const query = params.get(SEARCH_PARAM) ?? '';
  const status = readEnumParam(params, 'status', ACTIVITY_STATUSES);
  const onboarding = readEnumParam(params, 'onboarding', ONBOARDING_FILTERS);
  // A member row carries its workspace id and name itself; loader data is a stable reference.
  const workspace = useWorkspaceFilter(members, { clearWith: [ORGANIZATION_PARAM] });
  const organization = useOrganizationFilter(workspace.workspaceId, organizations);
  const serverEmpty = members.length === 0;

  const filtered = useMemo(
    () =>
      members.filter(
        (m) =>
          (status === null || m.status === status) &&
          (onboarding === null || (onboarding === 'complete') === (m.onboardingOpenCount === 0)) &&
          (workspace.workspaceId === null || m.workspaceId === workspace.workspaceId) &&
          // Any Standort of the member, not only the primary one.
          (organization.organizationId === null || m.organizationIds.includes(organization.organizationId)) &&
          matches(m, query.trim()),
      ),
    [members, status, onboarding, workspace.workspaceId, organization.organizationId, query],
  );
  const catalogue = useMemberColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.members,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={filtered.length} icon={MembersIcon} />} />
      <FilterBar
        fixedPills={
          <>
            <FilterPillGroup
              label={t('status-filter.label')}
              allLabel={t('status-filter.all')}
              icon={TagIcon}
              options={ACTIVITY_STATUSES.map((value) => ({ value, label: t(`status-filter.${value}`) }))}
              value={status}
              onChange={(next) => setParams({ status: next })}
              showLabel
            />
            <FilterPillGroup
              label={t('onboarding-filter.label')}
              allLabel={t('onboarding-filter.all')}
              icon={CheckSquareIcon}
              options={ONBOARDING_FILTERS.map((value) => ({ value, label: t(`onboarding-filter.${value}`) }))}
              value={onboarding}
              onChange={(next) => setParams({ onboarding: next })}
              showLabel
            />
            <TestWorkspacesPill />
          </>
        }
        pills={workspace.pills}
        {...(workspace.pills !== null ? { overflow: workspace.overflow } : {})}
        searchValue={query}
        searchPlaceholder={tFilters('search')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      {workspace.workspaceId !== null && <OrganizationFilterRow filter={organization} />}
      {filtered.length === 0 ? (
        <ListEmptyState title={serverEmpty ? t('empty') : t('empty-filtered')} serverEmpty={serverEmpty} />
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((member) => (
              <TableRow key={member.id} interactive onClick={() => navigate(`/workspaces/members/${member.id}`)}>
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(member)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
